import { useState } from 'react'

import { Play } from 'lucide-react'

import { createColDefs } from '@fortem-monorepo/react-ag'
import { defaultGetRowId } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { TimeCellRenderer } from '@fortem-monorepo/react-ag'
import { useWindowTabs } from '@fortem-monorepo/react-hooks'
import { DelayRender } from '@fortem-monorepo/react-spa'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { WindowTabButton } from '@fortem-monorepo/react-spa'
import { DatePickerWithControls } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Logs } from '@fortem-monorepo/web-sdk'

import { ScheduledTaskStatusCellRenderer } from '@/ag-grid/cell-renderers/ScheduledTaskStatusCellRenderer'
import { Page } from '@/components/Page'
import { RunManualTaskModal } from '@/components/dialogs/RunManualTaskModal'
import { MONITORING } from '@/constants/links'
import {
  monitoringContext,
  useMonitoringContextCreator,
} from '@/pages/monitoring/_dashboard/components/MonitoringContext'
import SummaryTab from '@/pages/monitoring/_dashboard/components/SummaryTab'

const colDefs = createColDefs<Logs.ScheduledTask>([
  { field: 'id', hide: true, resizable: true },
  { field: 'date', width: 100, hide: true, sortable: true },
  { field: 'description', width: 250, sortable: true },
  { field: 'frequency', width: 125, sortable: true },
  {
    field: 'upload_datetime_utc',
    headerName: 'Started At UTC',
    width: 175,
    cellRenderer: TimeCellRenderer,
  },
  {
    field: 'update_datetime_utc',
    headerName: 'Finished At UTC',
    width: 175,
    cellRenderer: TimeCellRenderer,
  },
  {
    field: 'status',
    width: 100,
    cellRenderer: ScheduledTaskStatusCellRenderer,
  },
  { field: 'output', width: 600 },
])

export interface MonitoringDashboardPageProps {}

export const MonitoringDashboardPage: React.FC<MonitoringDashboardPageProps> = ({}) => {
  const { windowTabs, activeTab, addWindowTab, closeWindowTab, setActiveTabId } = useWindowTabs({
    tabs: [
      {
        id: 'summary',
        title: 'Summary',
        pinned: true,
        component: <SummaryTab />,
      },
    ],
    activeTabId: 'summary',
  })

  const context = useMonitoringContextCreator({ addWindowTab })

  const { scheduledTasksQuery, selectedDate, setSelectedDate, visibleTasks, hasDataForDate } =
    context

  const [runTaskOpen, setRunTaskOpen] = useState(false)

  return (
    <Page metadata={{ title: 'Monitoring' }} permissions={MONITORING.MONITORING.permissions}>
      <RunManualTaskModal open={runTaskOpen} onOpenChange={setRunTaskOpen} />
      <monitoringContext.Provider value={context}>
        <HeaderConfigBar>
          <div className={'flex gap-2 items-center'}>
            <DatePickerWithControls
              size="sm"
              todayButton={{ enabled: true }}
              disabled={(date) => !hasDataForDate(date)}
              selected={selectedDate}
              onSelect={(date) => {
                date && setSelectedDate(date)
              }}
            />
            <Button size="sm" onClick={() => setRunTaskOpen(true)} variant={'outline'}>
              Run task <Play />
            </Button>
          </div>
        </HeaderConfigBar>
        <HeaderConfigBar noPadding>
          {windowTabs.map((windowTab, index) => (
            <WindowTabButton
              key={index}
              windowTab={windowTab}
              onTabClick={() => setActiveTabId(windowTab.id)}
              onTabClose={() => closeWindowTab(windowTab.id)}
              isActive={activeTab?.id === windowTab?.id}
            />
          ))}
        </HeaderConfigBar>
        <div className={'flex-1'}>
          <ResizablePanelGroup direction={'vertical'}>
            <ResizablePanel minSize={17} className={'relative'}>
              <DelayRender key={activeTab?.id}>
                {activeTab?.component && activeTab.component}
              </DelayRender>
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel minSize={24}>
              <div className={'h-full border-t'}>
                <Widget exposed className={'h-full'}>
                  <WidgetHeaderWithQuery query={scheduledTasksQuery} title={'Scheduled Tasks'} />
                  <WidgetContent>
                    <CustomAgGrid
                      columnDefs={colDefs}
                      domLayout={'normal'}
                      rowData={visibleTasks}
                      getRowId={defaultGetRowId}
                    />
                  </WidgetContent>
                </Widget>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </monitoringContext.Provider>
    </Page>
  )
}
